import { createRouter, createWebHistory } from "vue-router";
import { auth, fetchSession, isAuthenticated } from "@/stores/auth";
import { i18n } from "@/i18n";
import { isStaleChunkError, reloadForStaleChunk, clearStaleChunkFlag } from "@/lib/reload";
import { reportError } from "@/lib/appsignal";

// Everyone lands in their personal contribution first; board members flip to the
// organisation view via the sidebar toggle. The marketing page owns "/", so the
// personal home is "/mijn-bijdrage".
const PERSONAL_HOME = "/mijn-bijdrage";

const routes = [
  // Public marketing root. Signed-in visitors are redirected to their dashboard home
  // by the guard below.
  { path: "/", name: "home", component: () => import("@/pages/Landing.vue"), meta: { public: true, title: "landing.title", description: "landing.description" } },

  // Public marketing pages, one search intent each (the homepage owns "automatische
  // incasso van VvE-bijdragen"; these own the manager and the arrears query). Signed-in
  // visitors are NOT bounced off these — only `name: "home"` is — so a customer can
  // still read them.
  { path: "/voor-vve-beheerders", name: "vve-beheerders", component: () => import("@/pages/VoorVveBeheerders.vue"), meta: { public: true, title: "beheerders.title", description: "beheerders.description" } },
  { path: "/vve-betalingsachterstand", name: "vve-betalingsachterstand", component: () => import("@/pages/VveBetalingsachterstand.vue"), meta: { public: true, title: "achterstand.title", description: "achterstand.description" } },

  // Compliance pages: public, no auth required. Terms and privacy are linked from the
  // landing footer; the DPA is only linked from the dashboard (it's only relevant once
  // you're a customer) but stays reachable at its URL for anyone who's been sent it.
  { path: "/algemene-voorwaarden", name: "voorwaarden", component: () => import("@/pages/AlgemeneVoorwaarden.vue"), meta: { public: true, title: "voorwaarden.title", description: "voorwaarden.description" } },
  { path: "/privacybeleid", name: "privacybeleid", component: () => import("@/pages/Privacybeleid.vue"), meta: { public: true, title: "privacybeleid.title", description: "privacybeleid.description" } },
  { path: "/verwerkersovereenkomst", name: "verwerkersovereenkomst", component: () => import("@/pages/Verwerkersovereenkomst.vue"), meta: { public: true, title: "verwerkersovereenkomst.title", description: "verwerkersovereenkomst.description" } },

  { path: "/login", name: "login", component: () => import("@/pages/auth/SignIn.vue"), meta: { public: true, guestOnly: true, title: "signin.title" } },
  { path: "/signup", name: "signup", component: () => import("@/pages/auth/SignUp.vue"), meta: { public: true, guestOnly: true, title: "signup.title" } },
  { path: "/forgot-password", name: "forgot", component: () => import("@/pages/auth/ForgotPassword.vue"), meta: { public: true, title: "forgot.title" } },
  { path: "/reset-password", name: "reset", component: () => import("@/pages/auth/ResetPassword.vue"), meta: { public: true, title: "reset.title" } },
  { path: "/uitnodiging", name: "invitation", component: () => import("@/pages/auth/InvitationAccept.vue"), meta: { public: true, title: "invitation.title" } },
  { path: "/verify-email", name: "verify-email", component: () => import("@/pages/auth/VerifyEmail.vue"), meta: { public: true, title: "verify_email.title" } },
  { path: "/organisations", name: "organisations", component: () => import("@/pages/Organisations.vue"), meta: { title: "organisations.title" } },

  // The three old auth-style settings routes are gone, unified into "/instellingen"
  // below — these stay as redirects so any stale bookmark/link keeps working.
  { path: "/settings/password", redirect: "/instellingen" },
  { path: "/settings/email", redirect: "/instellingen" },
  { path: "/settings/security", redirect: "/instellingen" },

  // The dashboard shell. DashboardLayout is the persistent parent (sidebar + header +
  // inner <router-view/>) so it survives navigation between sections. Its own path
  // ("/app") is just a layout host that is never linked: the section children declare
  // absolute paths so their URLs are unchanged, and "/app" itself redirects to the
  // board home.
  {
    path: "/app",
    component: () => import("@/layouts/DashboardLayout.vue"),
    children: [
      { path: "", redirect: PERSONAL_HOME },
      { path: "/overzicht", name: "overzicht", component: () => import("@/pages/dashboard/Overzicht.vue"), meta: { section: "org", title: "nav.overzicht" } },
      { path: "/eenheden", name: "eenheden", component: () => import("@/pages/dashboard/Eenheden.vue"), meta: { section: "org", title: "nav.eenheden" } },
      { path: "/team", name: "team", component: () => import("@/pages/dashboard/Team.vue"), meta: { section: "org", title: "nav.team" } },
      { path: "/opvolgen", name: "opvolgen", component: () => import("@/pages/dashboard/Opvolgen.vue"), meta: { section: "org", title: "nav.opvolgen" } },
      { path: "/facturen", name: "facturen", component: () => import("@/pages/dashboard/Facturen.vue"), meta: { section: "org", title: "nav.facturen" } },
      { path: "/mijn-bijdrage", name: "mijn-bijdrage", component: () => import("@/pages/dashboard/MijnBijdrage.vue"), meta: { section: "personal", title: "nav.mijn_bijdrage" } },
      // The whole personal side is one page now. These two stay as redirects so mails,
      // bookmarks and Mollie's return URL keep working — same pattern as the settings
      // redirects above.
      { path: "/machtiging", redirect: "/mijn-bijdrage" },
      { path: "/betalingen", redirect: "/mijn-bijdrage" },
      { path: "/instellingen", name: "settings", component: () => import("@/pages/dashboard/Instellingen.vue"), meta: { title: "nav.settings" } },
    ],
  },

  { path: "/:pathMatch(.*)*", redirect: "/" },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(async (to) => {
  if (!auth.ready) await fetchSession();
  const authed = isAuthenticated();
  const role = auth.membership?.role;

  // Signed-in visitors skip the marketing root and the guest-only auth pages — send
  // them straight to their personal home (board flips to org from the sidebar).
  if (authed && (to.name === "home" || to.meta.guestOnly)) {
    return PERSONAL_HOME;
  }
  // Everything outside the public set needs a session.
  if (!to.meta.public && !authed) {
    return { name: "login", query: { next: to.fullPath } };
  }
  // Org sections are board-only (enforced server-side too); a non-board deep link
  // lands on the personal home it can actually use, not a 403 shell. Personal
  // sections are open to every member, so they need no gate.
  if (authed && to.meta.section === "org" && role !== "board") {
    return PERSONAL_HOME;
  }
  return true;
});

// A tab left open across a deploy still points at the previous build's hashed chunk
// files; when it lazy-loads a route those files are gone and the navigation rejects.
// Reload once to pick up the new build instead of dying on a dead link. Anything else
// is a real bug — the router never surfaces to app.config.errorHandler, so hand it to
// reportError ourselves rather than letting it disappear silently (which is what it
// still does while error tracking is switched off; see lib/appsignal).
router.onError((error, to) => {
  if (isStaleChunkError(error) && reloadForStaleChunk(to?.fullPath)) return;
  reportError(error);
});

// Give each route a meaningful browser-tab title instead of a bare "Pandig". Public
// pages also get their own meta description (SEO), swapped in over the Rails-
// rendered default from the same route meta; other routes are noindex'd (see
// robots.txt) and keep the app-wide default rather than fetching one per route.
router.afterEach((to) => {
  // We navigated fine, so any earlier stale-chunk reload has done its job — reset the
  // one-shot guard for a future deploy.
  clearStaleChunkFlag();

  // " | Pandig", matching the titles the Rails layout renders server-side (and that a
  // crawler or a link preview sees first). This used to be a middot, so the same page
  // changed its own title the moment JavaScript ran — one page, two titles, and a
  // search result built from whichever one got indexed.
  document.title = to.meta.title ? `${i18n.global.t(to.meta.title)} | Pandig` : "Pandig";

  if (to.meta.public && to.meta.description) {
    const description = i18n.global.t(to.meta.description);
    for (const selector of ['meta[name="description"]', 'meta[property="og:description"]', 'meta[name="twitter:description"]']) {
      document.querySelector(selector)?.setAttribute("content", description);
    }
  }
});
